import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import SvgIcon from "../SvgIcon";
import useClickOutside from "../../hooks/useClickOutside";
import { computeOverlayListPosition } from "../../hooks/useDropdownOverlay";

const filterOptions = (searchString = "", options, key) => {
  const words = searchString.split(" ");
  const filtered = options.filter((option) => {
    return words.reduce(
      (acc, curr) =>
        acc &&
        option[key] &&
        String(option[key]).toLowerCase().includes(curr.toLowerCase()),
      true
    );
  });
  return filtered;
};

const getOptionLabel = (option) => option.movementLabel || option.label;

export const SearchDropdown = ({
  handleChange,
  options = [],
  placeholder,
  valueKey = "value",
  value = "",
  disabled = false,
  onOpenChange, // Optional callback when dropdown opens/closes
  // When true, the list is rendered in a portal with fixed positioning so it
  // floats above scroll containers and footers (e.g. modals) instead of
  // expanding them — same pattern as parts/dropdown.js / useDropdownOverlay.
  overlay = false,
  // Focus the closed summary on mount so a coach can type an exercise
  // without clicking first.
  autoFocus = false,
  // Printable keys and Enter/Space open the search from the summary.
  typeAhead = false,
  // id for the closed summary so other code can move focus to the picker.
  summaryId,
}) => {
  const { t } = useTranslation("coaching-portal");
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const summaryRef = useRef(null);
  const listRef = useRef(null);
  const [search, setSearch] = useState("");
  const [filteredList, setFilteredList] = useState([]);
  const [searchOpened, setSearchOpened] = useState(false);
  const [currentOptionIndex, setCurrentOptionIndex] = useState(0);
  const [listPosition, setListPosition] = useState(null);
  const resolvedPlaceholder = placeholder ?? t("searchDropdown.selectOption");
  useClickOutside(
    dropdownRef,
    () => {
      setSearchOpened(false);
    },
    searchOpened,
    // The portaled list isn't a DOM descendant of dropdownRef, so exclude it
    // explicitly — otherwise a click on an option would be treated as
    // "outside" on mousedown and unmount the list before its own click
    // handler (selection) ever runs.
    overlay && listRef.current ? [listRef.current] : []
  );
  useEffect(() => {
    search.length > 0
      ? setFilteredList(filterOptions(search, options, valueKey))
      : setFilteredList(options);
  }, [options, search, valueKey]);

  // Notify parent when dropdown opens/closes
  useEffect(() => {
    if (onOpenChange) {
      onOpenChange(searchOpened);
    }
  }, [searchOpened, onOpenChange]);

  // When overlay is on, position the portaled list from the summary's rect.
  // Reuses useDropdownOverlay's positioning math directly (rather than the
  // whole hook) since this component keeps its own richer open/search state.
  // biome-ignore lint/correctness/useExhaustiveDependencies: filteredList is a remeasure trigger (its length affects the portaled list's height) — intentionally in the deps though it's not read in this closure
  useLayoutEffect(() => {
    if (!overlay || !searchOpened || !summaryRef.current) {
      setListPosition(null);
      return undefined;
    }

    const placeList = () => {
      const rect = summaryRef.current.getBoundingClientRect();
      const listWidth =
        listRef.current?.getBoundingClientRect().width ?? rect.width;
      setListPosition(
        computeOverlayListPosition({
          rect,
          listWidth,
          alignRight: false,
          openUpward: false,
        })
      );
    };

    placeList();

    if (!listRef.current) {
      const frameId = requestAnimationFrame(placeList);
      return () => cancelAnimationFrame(frameId);
    }

    return undefined;
  }, [overlay, searchOpened, filteredList]);

  // Close the floating list on outside scroll/resize — it's position: fixed
  // once portaled, so it could otherwise visually detach from its anchor.
  useEffect(() => {
    if (!overlay || !searchOpened) return undefined;
    const handleScroll = (e) => {
      if (listRef.current?.contains(e.target)) return;
      setSearchOpened(false);
    };
    const handleResize = () => setSearchOpened(false);
    window.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [overlay, searchOpened]);

  useEffect(() => {
    if (!autoFocus || disabled) return;
    summaryRef.current?.focus();
  }, [autoFocus, disabled]);

  //select an option
  const onChange = (value) => {
    handleChange(value);
    setSearchOpened(false);
    setSearch("");
  };

  const handleClick = (e) => {
    e.preventDefault();
    if (!searchOpened && !disabled) {
      setSearchOpened(true);
      focusInput();
    }
  };
  const toggleDropdown = () => {
    setSearchOpened(!searchOpened);
    if (searchOpened) {
      setSearch("");
    }
  };
  const focusInput = (immediate = false) => {
    const run = () => inputRef?.current?.focus();
    if (immediate) {
      requestAnimationFrame(run);
      return;
    }
    setTimeout(run, 500);
  };
  const onSummaryKeyDown = (event) => {
    // Keys from the open search input bubble through <summary>. Ignore them
    // so typing doesn't reset the query back to a single character.
    if (
      !typeAhead ||
      disabled ||
      searchOpened ||
      event.nativeEvent.isComposing
    ) {
      return;
    }
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      event.stopPropagation();
      setSearchOpened(true);
      focusInput(true);
      return;
    }
    if (
      event.key.length === 1 &&
      !event.ctrlKey &&
      !event.metaKey &&
      !event.altKey
    ) {
      event.preventDefault();
      event.stopPropagation();
      setSearch(event.key);
      setSearchOpened(true);
      focusInput(true);
    }
  };
  const handleKeyPress = (event) => {
    const { key } = event;

    if (searchOpened) {
      switch (key) {
        case "Escape":
          toggleDropdown();
          break;
        case "ArrowDown":
          moveFocusDown();
          break;
        case "ArrowUp":
          moveFocusUp();
          break;
        case "Enter":
          onChange(filteredList[currentOptionIndex]);
          break;
        default:
          break;
      }
    }
  };

  const moveFocusDown = () => {
    if (currentOptionIndex < filteredList.length - 1) {
      setCurrentOptionIndex(currentOptionIndex + 1);
    } else {
      setCurrentOptionIndex(0);
    }
  };

  const moveFocusUp = () => {
    if (currentOptionIndex > 0) {
      setCurrentOptionIndex(currentOptionIndex - 1);
    } else {
      setCurrentOptionIndex(elements.options.length - 1);
    }
  };
  const overlayList =
    overlay && searchOpened && listPosition
      ? createPortal(
          <ul
            ref={listRef}
            role="listbox"
            className="dropdown-overlay-list opened"
            style={{
              position: "fixed",
              ...(listPosition.top != null ? { top: listPosition.top } : {}),
              ...(listPosition.bottom != null
                ? { bottom: listPosition.bottom }
                : {}),
              ...(listPosition.left != null ? { left: listPosition.left } : {}),
              ...(listPosition.right != null
                ? { right: listPosition.right }
                : {}),
              minWidth: listPosition.minWidth,
              maxHeight: listPosition.maxHeight,
              zIndex: 10000,
            }}
          >
            {filteredList.map((option, i) => (
              <li
                key={`${i}${getOptionLabel(option)}`}
                onClick={() => onChange(option)}
                role="option"
                className={currentOptionIndex === i ? "current" : ""}
              >
                <div>{getOptionLabel(option)}</div>
              </li>
            ))}
          </ul>,
          document.body
        )
      : null;

  return (
    <details ref={dropdownRef} role="list" open className="dropdown-img">
      <summary
        id={summaryId}
        ref={summaryRef}
        onClick={handleClick}
        onKeyDown={onSummaryKeyDown}
        className={disabled ? "disabled" : ""}
      >
        {searchOpened ? (
          <input
            ref={inputRef}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (["Enter", "Tab"].includes(e.key)) {
                e.preventDefault();
                e.stopPropagation();
              }
              handleKeyPress(e);
            }}
          />
        ) : (
          <p>{value || resolvedPlaceholder}</p>
        )}
        <SvgIcon
          src={
            searchOpened ? "/img/svg/angle-up.svg" : "/img/svg/angle-down.svg"
          }
        />
      </summary>
      {overlay ? (
        overlayList
      ) : (
        <ul role="listbox" className={searchOpened ? "opened" : ""}>
          {filteredList.map((option, i) => (
            <li
              key={`${i}${getOptionLabel(option)}`}
              onClick={() => onChange(option)}
              role="option"
              className={currentOptionIndex === i ? "current" : ""}
            >
              <span>{getOptionLabel(option)}</span>
            </li>
          ))}
        </ul>
      )}
    </details>
  );
};
